import { Button, Stack, Text } from '@components/ui';
import { formatCooldown, useOtpCooldown } from '@features/auth/otpCooldown';
import { apiErrorMessage } from '@lib/api/apiError';
import { useSendOtpMutation, useVerifyOtpMutation } from '@lib/api/auth';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import OtpInput from 'react-otp-input';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface OtpVerificationProps {
  handleShowOtpVerificationModal: () => void;
  forgetPassword?: boolean;
  phoneNumber: string;
  userRole?: string;
  capabilities?: string[];
  itemId?: string | null;
}


const OtpVerification = ({
  handleShowOtpVerificationModal,
  forgetPassword = false,
  phoneNumber,
  userRole = 'buyer',
  capabilities = [],
  itemId = null,
}: OtpVerificationProps): JSX.Element => {
  const { t } = useTranslation();
  const navigator = useNavigate();
  const verifyOtp = useVerifyOtpMutation();
  const sendOtp = useSendOtpMutation();
  const [otp, setOtp] = useState('');
  const cooldown = useOtpCooldown(phoneNumber);

  const handleClose = (): void => {
    handleShowOtpVerificationModal();
  };

  const handleVerifyOtp = (): void => {
    verifyOtp
      .mutateAsync({ phoneNumber, otp })
      .then(() => {
        handleShowOtpVerificationModal();
        const title = forgetPassword
          ? 'auth.resetPassword'
          : 'auth.setPassword';
        navigator(itemId ? `/set-password?itemID=${itemId}` : '/set-password', {
          state: { phoneNumber, title },
        });
      })
      .catch((error: unknown) => {
        toast.error(
          apiErrorMessage(error) || 'Something went wrong'
        );
      });
  };

  const handleResendOtp = (): void => {
    if (cooldown.coolingDown || sendOtp.isPending) {
      return;
    }
    sendOtp
      .mutateAsync({
        phoneNumber,
        userRole,
        capabilities,
        forgetPasswordOTP: forgetPassword,
      })
      .then(() => {
        cooldown.start();
        toast.success(String(t('auth.otpSent')));
      })
      .catch((error: unknown) => {
        toast.error(
          apiErrorMessage(error) || 'Something went wrong'
        );
      });
  };

  return (
    <>
      <Dialog
        open
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        className="otp-modal"
        PaperProps={{
          sx: {
            borderRadius: '24px',
            bgcolor: 'background.paper',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogContent>
          <IconButton
            type="button"
            aria-label={t('common.close')}
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Text variant="title" align="center" sx={{ mt: 2 }}>
            {t('auth.verifyOtp')}
          </Text>
          <Text
            variant="body"
            tone="muted"
            align="center"
            sx={{ mt: 1, mb: 3, fontSize: '0.875rem' }}
          >
            {t('auth.otpPrompt', { phone: phoneNumber })}
          </Text>
          <Box className="otp-input-container" sx={{ mb: 3 }}>
            <OtpInput
              value={otp}
              onChange={setOtp}
              numInputs={4}
              isInputNum
              shouldAutoFocus
              containerStyle={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
              }}
              inputStyle={{
                width: '56px',
                height: '56px',
                borderRadius: '12px',
                border: '2px solid rgb(var(--line))',
                background: 'rgb(var(--field))',
                color: 'rgb(var(--ink))',
                fontSize: '24px',
                fontWeight: '600',
              }}
              focusStyle={{
                borderColor: 'rgb(var(--primary))',
                outline: 'none',
              }}
              separator={
                <span
                  style={{
                    color: 'rgb(var(--muted))',
                    fontSize: '18px',
                    fontWeight: '600',
                    margin: '0 2px',
                  }}
                >
                  –
                </span>
              }
            />
          </Box>
          <Stack
            direction="row"
            align="center"
            justify="center"
            sx={{ mb: 2.5 }}
          >
            <Text variant="body" tone="muted" sx={{ fontSize: '0.875rem' }}>
              {t('auth.otpMissing')}
            </Text>
            {cooldown.coolingDown ? (
              <Text
                variant="body"
                aria-live="polite"
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {t('auth.resendIn', {
                  time: formatCooldown(cooldown.remaining),
                })}
              </Text>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleResendOtp}
                loading={sendOtp.isPending}
                sx={{
                  minWidth: 0,
                  p: 0,
                  color: 'primary.main',
                  fontWeight: 600,
                  '&:hover': {
                    color: 'primary.dark',
                    textDecoration: 'underline',
                  },
                }}
              >
                {t('auth.resendOtp')}
              </Button>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleVerifyOtp}
            disabled={otp.length < 4 || sendOtp.isPending}
            loading={verifyOtp.isPending}
          >
            {t('auth.verify')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OtpVerification;
